import { Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { BaseService } from '../../abstract/base.service';
import { StateReportOptions } from '../data-models/state-report-options.model';
import { StateReportType } from '../data-models/state-report-type.model';
import { StateReport } from '../data-models/state-report.model';
import { ReportDatesRequest } from '../request-models/report-dates-request.model';
import { StateReportBatchRequest } from '../request-models/state-report-batch-request.model';
import { StateReportFilters } from '../request-models/state-report-filters.model';
import { Page } from '../response-models/page.model';
import { ReportBatchResult } from '../response-models/report-batch-result.model';
import { StateReportQueueStats } from '../response-models/state-report-queue-stats.model';
import { StateReportWithJobs } from '../response-models/state-report-with-jobs.model';
import { isRecord } from '../utils/is-record';
import { toQueueStats, toReportBatchResult, toReportJob } from '../utils/report-wire-utils';
import { listOf } from '../utils/wire-value-utils';

/**
 * State compliance reports across all organizations: `/admin/state-reports` (hub
 * `admin-state-reports-controller.ts`), paged with `limit`. An organization's report states come from
 * `OrganizationService.getReportStates()`. Failures reject with an `ApiError`.
 */
@Injectable({ providedIn: 'root' })
export class StateReportService extends BaseService<StateReport> {
  constructor() {
    super('admin/state-reports', StateReport);
  }

  /** GET /admin/state-reports: one page of reports (`filters.pageSize` is sent as `limit`). */
  listReports(filters: StateReportFilters = {}): Promise<Page<StateReport>> {
    const query = {
      page: filters.page,
      limit: filters.pageSize,
      organizationId: filters.organizationId || undefined,
      status: filters.status || undefined,
      state: filters.state || undefined,
      startDate: filters.startDate || undefined,
      endDate: filters.endDate || undefined,
      search: filters.search?.trim() || undefined,
    };
    return firstValueFrom(this.getPage({ query }));
  }

  /** GET /admin/state-reports/:id: the report with its download URL (when ready) and generation jobs. */
  getReport(reportId: string): Promise<StateReportWithJobs | null> {
    return firstValueFrom(this.request('GET', { id: reportId }, toReportWithJobs));
  }

  /** DELETE /admin/state-reports/:id (soft delete; refused while generating). */
  deleteReport(reportId: string): Promise<void> {
    return firstValueFrom(this.delete(reportId));
  }

  /** POST /admin/state-reports/:id/regenerate: queues a new generation job. */
  regenerateReport(reportId: string): Promise<void> {
    return firstValueFrom(this.request('POST', { id: reportId, path: 'regenerate' }, () => undefined, {}));
  }

  /** POST /admin/state-reports/:id/retry: retries a failed generation job. */
  retryReport(reportId: string): Promise<void> {
    return firstValueFrom(this.request('POST', { id: reportId, path: 'retry' }, () => undefined, {}));
  }

  /** GET /admin/state-reports/queue-stats */
  getQueueStats(): Promise<StateReportQueueStats> {
    return firstValueFrom(this.request('GET', { path: 'queue-stats' }, toQueueStats));
  }

  /** POST /admin/state-reports/batch: one report per state of each organization's fields. */
  generateBatch(
    organizationIds: string[],
    startDate: string,
    endDate: string,
    reportType: StateReportType,
    options: StateReportOptions
  ): Promise<ReportBatchResult<StateReport>> {
    const body: StateReportBatchRequest = { organizationIds, startDate, endDate, reportType, options };
    return firstValueFrom(this.request('POST', { path: 'batch' }, data => toReportBatchResult(data, toReport), body));
  }

  /** PUT /admin/state-reports/:id: the only writable fields are the two dates. */
  updateReportDates(reportId: string, startDate: string, endDate: string): Promise<StateReport | null> {
    const body: ReportDatesRequest = { startDate, endDate };
    return firstValueFrom(this.update(reportId, body));
  }
}

function toReport(data: unknown): StateReport {
  return new StateReport(data);
}

function toReportWithJobs(data: unknown): StateReportWithJobs | null {
  if (!isRecord(data) || !isRecord(data['report'])) {
    return null;
  }
  return Object.assign(new StateReport(data['report']), { jobs: listOf(data['jobs'], toReportJob) });
}
